import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FavoriteCard } from './FavoriteCard';
import { OfferSearchResult } from '@/features/search/types/search.types';
import { AppButton } from '@/shared/components/buttons/AppButton';
import { favoritesService } from '../services';
import { useFavoritesCountContext } from '../context/FavoritesCountContext';

const PAGE_LIMIT = 5;

export function FavoritesPage() {
  const { t } = useTranslation();
  const { refreshCount } = useFavoritesCountContext();
  const [favorites, setFavorites] = useState<OfferSearchResult[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFavorites = useCallback(
    async (nextPage: number, append: boolean = false) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }
        setError(null);
        const response = await favoritesService.getFavoriteOffers(nextPage, PAGE_LIMIT);
        setFavorites(prev => (append ? [...prev, ...response.data] : response.data));
        setPage(response.meta.page);
        setHasMore(response.meta.page < response.meta.totalPages);
      } catch (err) {
        console.error('Failed to load favorites', err);
        setError(t('favorites.error'));
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [t]
  );

  // Initial load
  useEffect(() => {
    loadFavorites(1);
  }, [loadFavorites]);

  // Scroll to top when page is accessed
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLoadMore = () => {
    if (!isLoadingMore && !isLoading) {
      loadFavorites(page + 1, true);
    }
  };

  const handleFavoriteRemoved = useCallback(
    (offerId: string, isFavorite: boolean) => {
      if (!isFavorite) {
        // Favorite was removed, filter it out from the list
        setFavorites(prev => prev.filter(offer => offer.id !== offerId));
        refreshCount();
      }
    },
    [refreshCount]
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', mt: 4 }}>
      <Container maxWidth="md" sx={{ px: 2, pb: 10, pt: 0 }}>
        {isLoading && favorites.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {!isLoading && error ? (
          <Box sx={{ mt: 3 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : null}

        {!isLoading && !error && favorites.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 2,
              backgroundColor: 'grey.50',
              borderRadius: '10px',
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('favorites.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('favorites.emptyHint')}
            </Typography>
          </Box>
        ) : null}

        <Box>
          {favorites.map(offer => (
            <FavoriteCard key={offer.id} offer={offer} onFavoriteUpdate={handleFavoriteRemoved} />
          ))}
        </Box>

        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <AppButton variant="outlined" onClick={handleLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? t('common.loading') : t('favorites.loadMore')}
            </AppButton>
          </Box>
        )}
      </Container>
    </Box>
  );
}
